import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
  SzBox,
  SzButton,
  SzDatePicker,
  SzIcon,
  SzInput,
  SzPagination,
  SzSelect,
  SzTypographie,
} from "@suezenv/react-theme-components";
import EventService from "../services/EventService";
import EquipmentService from "../services/EquipmentService";
import EventTable from "../components/Event/EventTable";
import { formatLocalDateTime } from "../../main/utils";
import {
  PAGINATION_CURRENT_PAGE,
  PAGINATION_PAGE_COUNT,
  PAGINATION_PER_PAGE,
  PAGINATION_TOTAL_COUNT,
  ENCODING_CSV_UTF8,
} from "../../main/store/constants";
import { displayValues } from "../store/tools";
import { routesPath } from "../../routes";
import * as services from "../../services/services";
import AlertBox, { AlertBoxType } from "../../ui/alert/AlertBox";

const FIRST_PAGE: number = 1;
const DEFAULT_ITEMS_PER_PAGE: number = 20;
const DEFAULT_SORT_KEY: string = "date";
const DEFAULT_SORT_DIR: string = "desc";
const ITEMS_PER_PAGE: number[] = [20, 50, 100];
const PAGINATION_PAGE_RANGE_MAX: number = 10;

interface Params {
  paramPage: number;
  paramItemsPerPage: number;
  paramSortKey: string;
  paramSortDir: string;
  paramStartAt: string | null;
  paramEndAt: string | null;
  paramEventTypeKeys: string[];
  paramSearch: string;
  paramEquipmentId: string | null;
}

const getParamsFromUrl = (search: string): Params => {
  const searchParams = new URLSearchParams(search);

  const paramPage = searchParams.has("page") ? parseInt(searchParams.get("page")!) : FIRST_PAGE;
  const paramItemsPerPage = searchParams.has("itemsPerPage")
    ? parseInt(searchParams.get("itemsPerPage")!)
    : DEFAULT_ITEMS_PER_PAGE;
  const paramSortKey = searchParams.get("sortKey") ?? DEFAULT_SORT_KEY;
  const paramSortDir = searchParams.get("sortDir") ?? DEFAULT_SORT_DIR;
  const paramStartAt = searchParams.get("startAt");
  const paramEndAt = searchParams.get("endAt");
  const paramEventTypeKeys = searchParams.getAll('eventTypeKeys[]') ? searchParams.getAll('eventTypeKeys[]') : [];
  const paramSearch = searchParams.get("search") ?? "";
  const paramEquipmentId = searchParams.get("equipmentId") ?? null;

  return {
    paramPage,
    paramItemsPerPage,
    paramSortKey,
    paramSortDir,
    paramStartAt,
    paramEndAt,
    paramEventTypeKeys,
    paramSearch,
    paramEquipmentId,
  };
};

const EventManager: any = (props: any) => {
  const { t } = useTranslation();

  const history = useHistory();
  const location = useLocation();

  const {
    paramPage,
    paramItemsPerPage,
    paramSortKey,
    paramSortDir,
    paramStartAt,
    paramEndAt,
    paramEventTypeKeys,
    paramSearch,
    paramEquipmentId,
  } = getParamsFromUrl(location.search);

  // =====================================================
  // states
  // =====================================================
  const [locationSearch, setLocationSearch] = useState(location.search);
  const [eventData, setEventData] = useState([]);
  const [eventPagination, setEventPagination] = useState<any[string]>([]);
  const [eventCount, setEventCount] = useState<number>(0);
  const [eventTypes, setEventTypes] = useState<any>({});
  const [page, setPage] = useState<number>(paramPage);
  const [itemsPerPage, setItemsPerPage] = useState(paramItemsPerPage);
  const [sortKey, setSortKey] = useState<string>(paramSortKey);
  const [sortDir, setSortDir] = useState<string>(paramSortDir);
  const [search, setSearch] = useState<string>(paramSearch);
  const [startAt, setStartAt] = useState<Date | null>(paramStartAt != null ? new Date(paramStartAt) : null);
  const [endAt, setEndAt] = useState<Date | null>(paramEndAt != null ? new Date(paramEndAt) : null);
  const [eventTypeKeys, setEventTypeKeys] = useState<string[]>(paramEventTypeKeys);
  const [eventTypeOptions, setEventTypeOptions] = useState<object[]>([]);
  const [alert, setAlert] = useState<AlertBoxType | null>(null);
  const [equipments, setEquipments] = useState<Set<string>>(new Set([]));
  const [equipment, setEquipment] = useState<any>(null);
  const [equipmentId, setEquipmentId] = useState<string | null>(paramEquipmentId);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // =====================================================
  // life cycles
  // =====================================================
  useEffect(() => {
    loadEventTypes();

    if (isInitialLoad) {
        setIsInitialLoad(false);
    }

  }, []);

  useEffect(() => {
    updateUrl(page, itemsPerPage, sortKey, sortDir, startAt, endAt, eventTypeKeys, search, equipmentId);
  }, [page, itemsPerPage, sortKey, sortDir, startAt, endAt, eventTypeKeys.length, equipmentId]);

  useEffect(() => {
    // reset to first page when filter changes and only if page is loaded
    if(!isInitialLoad) {
        setPage(FIRST_PAGE);
    }
  }, [search, itemsPerPage, startAt, endAt, sortKey, sortDir, eventTypeKeys.length]);

  useEffect(() => {
    setEventTypeOptions([]);

    if (eventTypeKeys.length === 0 || Object.keys(eventTypes).length === 0) {
      return;
    }

    const typeOptions: any = [];
    Array.from(eventTypeKeys).forEach((eventTypeKey) => {
        const type = eventTypes.find((item: any) => item.type === eventTypeKey);
        if(type) {
            typeOptions.push({ value: type.type, label: type.label });
        }
    });

    setEventTypeOptions(typeOptions);
  }, [eventTypeKeys.length, Object.keys(eventTypes).length]);

  useEffect(() => {
    resetAlert();
    updateEventData();
  }, [locationSearch]);

  useEffect(() => {
    if (!location.search) {
      resetFilter();
    }
  }, [location.search]);

  // =====================================================
  // event handlers
  // =====================================================
  const resetHandler = () => {
    resetFilter();
    resetAlert();
    updateUrl(FIRST_PAGE, itemsPerPage, sortKey, sortDir);
  };

  const dateRangeResetHandler = () => {
    setStartAt(null);
    setEndAt(null);
  };

  const onChangeStartDateHandler = (date: Date | Date[]) => {
    setStartAt(null);
    if (date instanceof Date) {
        setStartAt(date);
    }
  };

  const onChangeEndDateHandler = (date: Date | Date[]) => {
    setEndAt(null);
    if (date instanceof Date) {
        setEndAt(date);
    }
  };

  const enterKeyHandler = (event: any) => {
    if (event.key === "Enter") {
      setSearch(event.target.value);
      updateUrl();
    }
  };

  // =====================================================
  // functions
  // =====================================================
  const getColumns = (): any[] => {
    return [
      columnJson("date", "event:list.date"),
      columnJson("type", "event:list.type", false),
      columnJson("description", "event:list.description", false),
      columnJson("equipment", "event:list.equipment", false),
    ];
  };

  const columnJson = (dataField: string, text: string, canSort = true, classes: string = "") => {
    const sortDirection = sortDir === "desc" ? "sz-icon-line arrow-down-1" : "sz-icon-line arrow-up-1";
    const headerDefaultClass = canSort ? "sz-icon-line move-expand-vertical" : "";
    return {
      classes,
      dataField,
      text: t(text),
      headerClasses: sortKey === dataField ? sortDirection : headerDefaultClass,
      headerEvents: {
        onClick: canSort ? sortByColumn : () => {},
      },
    };
  };

  const sortByColumn = (e: any, column: any) => {
    setSortKey(column.dataField);
    setSortDir(sortDir === "desc" ? "asc" : "desc");
  };

  const mergeStateFilters = () => {
    let filters = {};

    if (search) {
      filters = { ...filters, search };
    }
    if (startAt) {
      filters = { ...filters, startAt: startAt.toISOString() };
    }
    if (endAt) {
        // add one day to include end date
        let data = new Date(endAt.getTime());
        data.setDate(data.getDate() + 1);
        filters = { ...filters, endAt: data.toISOString() };
    }
    if (eventTypeKeys) {
      filters = { ...filters, types: eventTypeKeys };
    }

    return filters;
  };

  const hasFilter = () => {
    return search.length > 0 || startAt || endAt || eventTypeKeys.length > 0;
  };

  const resetFilter = () => {
    setPage(FIRST_PAGE);
    setSearch("");
    setEventTypeKeys([]);
    dateRangeResetHandler();
  };

  const resetAlert = () => {
    setAlert(null);
  };

  const getItemPerPageOptions = () => {
    return ITEMS_PER_PAGE.map((item) => (
      <option key={item} value={item}>
        {item}
      </option>
    ));
  };

  const getEventTypeOptions = () => {
    let options = Object.keys(eventTypes).map((key: any) => {
      const eventType: any = eventTypes[key];
      return { value: eventType.type, label: eventType.label };
    });

    // sort alphabetically
    options.sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });

    return options;
  };

  const onChangeEventTypeKeysHandler = (options: any) => {
    setEventTypeKeys([]);

    if(options) {
        setEventTypeKeys(options.map((option: any) => option.value));
    }

    setEventTypeOptions(options);
    }

  const updateUrl = (
    page: number = FIRST_PAGE,
    itemsPerPage: number = DEFAULT_ITEMS_PER_PAGE,
    sortKey: string = DEFAULT_SORT_KEY,
    sortDir: string = DEFAULT_SORT_DIR,
    startAt: Date | null = null,
    endAt: Date | null = null,
    eventTypeKeys: string[] | null = null,
    search: string = "",
    equipmentId: string | null = null,
  ) => {
    const urlParams: string | string[] = [];

    if (page) {
      urlParams.push(`page=${page}`);
    }
    if (itemsPerPage) {
      urlParams.push(`itemsPerPage=${itemsPerPage}`);
    }
    if (sortKey) {
      urlParams.push(`sortKey=${sortKey}`);
    }
    if (sortDir) {
      urlParams.push(`sortDir=${sortDir}`);
    }
    if (startAt) {
      urlParams.push(`startAt=${startAt.toISOString()}`);
    }
    if (endAt) {
      urlParams.push(`endAt=${endAt.toISOString()}`);
    }
    if (eventTypeKeys && eventTypeKeys.length) {
        urlParams.push(eventTypeKeys.map((eventTypeKey) => `eventTypeKeys[]=${eventTypeKey}`).join("&"));
    }
    if (search.length) {
      urlParams.push(`search=${encodeURI(search)}`);
    }
    if(equipmentId) {
        urlParams.push(`equipmentId=${equipmentId}`);
    }

    let url = routesPath.event;
    if (urlParams.length > 0) {
      url = url + "?" + urlParams.join("&");
    }

    setLocationSearch("?" + urlParams.join("&"));
    history.push(url);
  };

  // =====================================================
  // webservices calls
  // =====================================================
  const loadEventTypes = () => {
    EventService.getEventTypes()
      .then((response: any) => {
        setEventTypes(response.data);
      })
      .catch((error) => {
        setAlert({ type: "danger", message: services.useExtractResponseMessage(t, error) });
      });
  };

  const updateEventData = async () => {
    setEventData([]);
    setEventPagination([]);
    setEventCount(0);
    setEquipments(new Set([]));

    let eventResponse: any = [];
    let equipmentResponse: any = [];
    let equipmentIds: Set<string> = equipmentId ? new Set([equipmentId]) : new Set([]);

    try {
      // =====================================================
      // get equipments according to the search filter
      if (search.length || Array.from(equipmentIds).length) {
        const equipmentFilter: any = {
          status: null,
          equipment: Array.from(equipmentIds),
          locationReferences: [],
          query: search,
        };
        equipmentResponse = await EquipmentService.getEquipments(
          FIRST_PAGE,
          equipmentFilter,
          null,
          "status_state_changed_at",
          "desc",
          10000,
        );

        if (equipmentResponse.data.length === 0) {
          setAlert({ type: "info", message: "Aucun résultat ne correspond à votre recherche." });
          return;
        }

        equipmentIds = new Set(equipmentResponse.data.map((item: any) => item.id));

        if (equipmentId) {
          const equipment = equipmentResponse.data.find((item: any) => item.id === equipmentId);
          setEquipment(equipment);
        }
      }

      // =====================================================
      // get events
      const filters = mergeStateFilters();
      eventResponse = await EventService.getEvents(
        page,
        itemsPerPage,
        sortKey,
        sortDir,
        filters,
        Array.from(equipmentIds),
      );
      if (eventResponse.data.length === 0) {
        setAlert({ type: "info", message: "Aucun résultat ne correspond à votre recherche." });
        return;
      }

      // =====================================================
      // get equipments related to events
      if (Array.from(equipmentIds).length === 0) {
        equipmentIds = new Set(eventResponse.data.map((item: any) => item.equipmentId));

        const equipmentFilter: any = {
          status: null,
          equipment: Array.from(equipmentIds),
          locationReferences: [],
          query: "",
        };
        equipmentResponse = await EquipmentService.getEquipments(
          FIRST_PAGE,
          equipmentFilter,
          null,
          "status_state_changed_at",
          "desc",
          Array.from(equipmentIds).length,
        );
      }
    } catch (error) {
      setAlert({ type: "danger", message: services.useExtractResponseMessage(t, error) });
      return;
    }

    // save equipment ids
    setEquipments(equipmentIds);

    // build event data
    const eventData = eventResponse.data.map((event: any) => {

        let content: any[] = []
        if( !event.equipmentName && !event.equipmentSerialNumber) {
            content.push(<span>{t("event:list.equipment_unavailable")}</span>);
        }
        else if (!event.equipmentName) {
            content.push(<span>{t("event:list.equipment_name_unavailable")}</span>);
            content.push(<span>{event.equipmentSerialNumber}</span>);
        }
        else if (!event.equipmentSerialNumber) {
            content.push(<span>{event.equipmentName}</span>);
            content.push(<span>{t("event:list.equipment_serial_number_unavailable")}</span>);
        }
        else  {
            content.push(<span>{event.equipmentName}</span>);
            content.push(<span>{event.equipmentSerialNumber}</span>);
        }

        const equipementInfos = (
          <div className="event-column-equipment">
            {content.map((item, idx) => (
              <React.Fragment key={idx}>{item}</React.Fragment>
            ))}
          </div>
        );

        return {
            id: event.id,
            date: formatLocalDateTime(event.date),
            type: t(`event:types.${event.type}`),
            description: <pre>{displayValues(event.values)}</pre>,
            equipment: equipementInfos,
        };
    });

    setEventData(eventData);
    setEventPagination(eventResponse.pagination);
    setEventCount(parseInt(eventResponse.pagination[PAGINATION_TOTAL_COUNT]));
  };

  const exportEvents = () => {
    const filters = mergeStateFilters();

    let equipmentIds: string[] = [];
    if (search.length > 0) {
      equipmentIds = Array.from(equipments);
    } else if (equipmentId) {
      equipmentIds = [equipmentId];
    }

    EventService.getEvents(page, "all", "date", "desc", filters, equipmentIds, "csv")
      ?.then((response) => {
        const windowUrl = window.URL;
        const bom = new Uint8Array(ENCODING_CSV_UTF8);
        const blob = new Blob([bom, response.data], { type: "text/csv;charset=utf-8;" });
        const url = windowUrl.createObjectURL(blob);
        const date = new Date().toLocaleString("sv-SE").substring(0, 16).replace(/\s/g, "_").replace(":", "-");
        const link = document.createElement("a");
        link.setAttribute("download", `evenements_${date}.csv`);
        link.href = url;
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        setAlert({ type: "danger", message: services.useExtractResponseMessage(t, error) });
      });
  };

  return (
    <>
      {alert && (
        <div className="row ml-0 mr-4">
          <AlertBox {...alert} />
        </div>
      )}
      <SzBox className="row event-title-box" tag="div">
        <div className="row mb-2">
          <div className="row col-6 p-0">
            <div className="ml-2">
              <SzIcon variant="line" icon="calendar-3" />
            </div>
            <div className="col-md-8 event-title">
              <div>
                <SzTypographie color="inactive" variant="caption" className="m-0">
                  {t("event:list.type")}
                </SzTypographie>
                <SzTypographie>{t("event:list.title")}</SzTypographie>
              </div>
              <span className="event-count">{eventCount.toLocaleString()}</span>
            </div>
          </div>
          <div className="row col-6 left-block">
            <div className="col-7">
              <SzInput
                value={search}
                onChange={({ target }) => setSearch(target.value)}
                onKeyUp={enterKeyHandler}
                placeholder={t("equipment:select_location_reference")}
              />
            </div>
            <div className="col-1 ">
              <SzButton
                onClick={() =>
                  updateUrl(page, itemsPerPage, sortKey, sortDir, startAt, endAt, eventTypeKeys, search, equipment)
                }
                variant="secondary"
                icon="search"
              />
            </div>
            {hasFilter() && (
              <div className="col-1">
                <SzButton onClick={resetHandler} variant="secondary" icon="diagram-round" />
              </div>
            )}
            <div className="col-3 event-items-per-page">
              <label>Nbr résultats</label>
              <select className="" onChange={(e) => setItemsPerPage(parseInt(e.target.value))} value={itemsPerPage}>
                {getItemPerPageOptions()}
              </select>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="row col-3 event-equipment-details">
            {equipment && (
              <>
                <div>
                  <span>Équipement :</span>
                  <span>{equipment?.name}</span>
                  <span>{equipment?.serialNumber}</span>
                </div>
                <div 
                    className="col-auto p-0 ml-2" 
                    onClick={() => {
                        setEquipment(null)
                        setEquipmentId(null)
                    }}
                >
                  <SzIcon variant="line" icon="remove" />
                </div>
              </>
            )}
          </div>
          <div className="row col-9 left-block">
            <div className="col-5 event-date-range-picker">
                <SzDatePicker
                    startDate={startAt!}
                    endDate={endAt!}
                    dateRange={true}
                    onChangeStartDate={(date) => onChangeStartDateHandler(date)}
                    onChangeEndDate={(date) => onChangeEndDateHandler(date)}
                />
                {
                    (startAt || endAt) && (
                        <div className="col-auto p-0 ml-4 pr-3" onClick={dateRangeResetHandler}>
                            <SzIcon variant="line" icon="remove" />
                        </div>
                    )
                }
            </div>
            <div className="col-5">
                <SzSelect
                    className={"select-form-names mr-3"}
                    placeholder={t("event:filter.eventTypes")}
                    options={getEventTypeOptions()}
                    value={eventTypeOptions}
                    onChange={(option: any) => onChangeEventTypeKeysHandler(option)}
                    isSearchable={true}
                    isMulti={true}
                />
            </div>
          </div>
        </div>
      </SzBox>

      <SzBox className="row ml-2 mr-1">
        <div className="row list-table event-table">
          <EventTable columns={getColumns()} data={eventData} />
        </div>
        {eventPagination[PAGINATION_PAGE_COUNT] > 1 && (
          <div className="mt-3 mx-auto">
            <SzPagination
              totalItemsCount={parseInt(eventPagination[PAGINATION_TOTAL_COUNT], 10)}
              activePage={parseInt(eventPagination[PAGINATION_CURRENT_PAGE], 10)}
              onChange={(page: string) => setPage(parseInt(page))}
              itemsCountPerPage={parseInt(eventPagination[PAGINATION_PER_PAGE], 10)}
              pageRangeDisplayed={PAGINATION_PAGE_RANGE_MAX}
            />
          </div>
        )}
        <div className="row mx-0 mb-3 mt-1 pt-2 d-flex justify-content-end">
          <SzButton
            variant="secondary"
            className="mb-3 download-btn event-btn"
            icon="download-bottom"
            onClick={exportEvents}
            isDisabled={eventData.length === 0}
          >
            {t("export")}
          </SzButton>
        </div>
      </SzBox>
    </>
  );
};

export default EventManager;
